import {Component, inject, OnInit, signal} from '@angular/core';
import {User} from '../../../../shared/model/user';
import {UserUpdate} from '../../../../shared/model/user-update';
import {ApiResponse} from '../../../../shared/model/api-response';
import {UserService} from '../../../../shared/services/user.service';
import {Router} from '@angular/router';
import {AuthService} from '../../../../core/auth/auth.service';
import {finalize} from 'rxjs';
import {Messages} from '../../../../shared/constants/messages';
import {ProfileFormComponent} from '../profile-form/profile-form.component';

@Component({
  selector: 'app-profile-edit',
  standalone: true,
  imports: [ProfileFormComponent],
  templateUrl: './profile-edit.component.html',
  styleUrl: './profile-edit.component.css'
})
export class ProfileEditComponent implements OnInit {
  private userService: UserService = inject(UserService);
  private router: Router = inject(Router);
  private authService: AuthService = inject(AuthService);

  protected userData = signal<User | undefined>(undefined);
  protected isLoading = signal<boolean>(false);
  protected error = signal<string | null>(null);

  ngOnInit(): void {
    this.loadUserData();
  }

  private loadUserData(): void {
    this.isLoading.set(true);
    this.userService.getMyUserData()
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (response: ApiResponse<User>) => {
          if (response.success && response.data) {
            this.userData.set(response.data);
          } else {
            this.error.set(Messages.USER_DATA_NOT_FOUND);
          }
        }
      });
  }

  onSave(formValue: Partial<UserUpdate>) {
    this.isLoading.set(true);
    this.error.set(null);

    const updateData: Partial<UserUpdate> = {
      id: this.userData()?.id,
      username: formValue.username,
      email: formValue.email,
      bio: formValue.bio,
      role: formValue.role,
      website: formValue.website,
      city: formValue.city,
      vatNumber: formValue.vatNumber,
      birthDate: formValue.birthDate,
      lookingForGroup: formValue.lookingForGroup,
      lookingForPlayer: formValue.lookingForPlayer
    };

    const previousEmail = this.userData()?.email;

    this.userService.updateMyUserData(updateData as UserUpdate)
      .pipe(finalize(() => this.isLoading.set(false)))
      .subscribe({
        next: (response: ApiResponse<User>) => {
          if (response.success && response.data) {
            if (response.data.userStatus && !response.data.userStatus.active) {
              // Email changed and user needs to verify
              this.authService.logout();
              this.router.navigate(['/login'], {
                queryParams: { emailChanged: 'true' }
              });
            } else {
              this.userData.set(response.data);
              this.authService.setCurrentUser(response.data);
              this.router.navigate(['/profile']);
            }
          }
        },
      });
  }

  onCancel(): void {
    this.router.navigate(['/profile']);
  }
}
